"use client";

import { Check, Eye, EyeOff, X } from "lucide-react";
import { Link } from "next-view-transitions";
import { useEffect, useId, useMemo, useState } from "react";

import { Input } from "~/lib/shadcn/components/ui/input";
import { Label } from "~/lib/shadcn/components/ui/label";

export default function PasswordInput({
	label = "Password",
	name = "password",
	onChange,
	onlyInput = true,
	value,
	visible = false,
}: {
	label?: string;
	name?: string;
	onChange: () => void;
	onlyInput?: boolean;
	value: string;
	visible?: boolean;
}) {
	const id = useId();
	const [isVisible, setIsVisible] = useState<boolean>(visible);
	useEffect(() => {
		setIsVisible(visible);
	}, [visible]);
	const toggleVisibility = () => {
		setIsVisible((prevState) => !prevState);
	};
	const checkStrength = (pass: string) => {
		const requirements = [
			{ regex: /.{8,}/, text: "At least 8 characters" },
			{ regex: /[0-9]/, text: "At least 1 number" },
			{ regex: /[a-z]/, text: "At least 1 lowercase letter" },
			{ regex: /[A-Z]/, text: "At least 1 uppercase letter" },
		];
		return requirements.map((req) => ({
			met: req.regex.test(pass),
			text: req.text,
		}));
	};
	const strength = checkStrength(value);
	const strengthScore = useMemo(() => {
		return strength.filter((req) => req.met).length;
	}, [strength]);
	const getStrengthColor = (score: number) => {
		if (score === 0) return "bg-border";
		if (score <= 1) return "bg-red-500";
		if (score <= 2) return "bg-orange-500";
		if (score === 3) return "bg-amber-500";
		return "bg-emerald-500";
	};
	const getStrengthText = (score: number) => {
		if (score === 0) return "Enter a password";
		if (score <= 2) return "Weak password";
		if (score === 3) return "Medium password";
		return "Strong password";
	};
	const input = (
		<div className="relative">
			<Input
				aria-describedby={`${id}-description`}
				aria-invalid={strengthScore < 4}
				autoComplete="new-password"
				className="pe-9"
				id={id}
				name={name}
				onChange={onChange}
				placeholder="Password"
				type={isVisible ? "text" : "password"}
				value={value}
			/>
			<button
				aria-controls="password"
				aria-label={isVisible ? "Hide password" : "Show password"}
				aria-pressed={isVisible}
				className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-end rounded-e-lg text-white/80 outline-offset-2 transition-colors hover:text-white focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
				onClick={toggleVisibility}
				type="button"
			>
				{isVisible ? (
					<EyeOff aria-hidden="true" className="stroke-1" size={16} strokeWidth={2} />
				) : (
					<Eye aria-hidden="true" className="stroke-1" size={16} strokeWidth={2} />
				)}
			</button>
		</div>
	);
	if (onlyInput) return input;
	return (
		<div>
			<div className="space-y-2">
				<div className="flex items-center">
					<Label htmlFor="password">{label}</Label>
					<Link className="ml-auto text-sm underline-offset-4 hover:underline" href="/">
						Forgot your password?
					</Link>
				</div>
				{input}
			</div>
			<div
				aria-label="Password strength"
				aria-valuemax={4}
				aria-valuemin={0}
				aria-valuenow={strengthScore}
				className="mb-4 mt-3 h-1 w-full overflow-hidden rounded-full bg-border"
				role="progressbar"
				tabIndex={0}
			>
				<div
					className={`h-full ${getStrengthColor(strengthScore)} transition-all duration-500 ease-out`}
					style={{ width: `${(strengthScore / 4) * 100}%` }}
				/>
			</div>
			<p className="mb-2 text-sm font-medium text-foreground" id={`${id}-description`}>
				{getStrengthText(strengthScore)}. Must contain:
			</p>
			<ul aria-label="Password requirements" className="space-y-1.5">
				{strength.map((req, index) => (
					<li className="flex items-center gap-2" key={`${index.toString()}-password-strength`}>
						{req.met ? (
							<Check aria-hidden="true" className="text-emerald-500 stroke-1" size={16} />
						) : (
							<X aria-hidden="true" className="text-muted-foreground/80 stroke-1" size={16} />
						)}
						<span className={`text-xs ${req.met ? "text-emerald-600" : "text-muted-foreground"}`}>
							{req.text}
							<span className="sr-only">{req.met ? " - Requirement met" : " - Requirement not met"}</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
